import React, { useState, useCallback } from "react"
import { ReactComponent as ForgotPasswordIcon } from '../../assets/icons/forgot-password.svg';
import { Button, Form, Input, Typography, Divider, notification } from "antd";
import { useHistory } from "react-router-dom";
import ajaxService from "../../services/ajax-service";
import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import AuthLayout from './AuthLayout';

const { Title, Text } = Typography

const ForgotPassword = () => {
    const history = useHistory()

    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);

    const showSuccess = useCallback((message) => {
        notification.open({
            message,
            icon: <CheckCircleOutlined style={{ color: 'var(--primary-purple)' }} />,
            duration: 3,
        });
    }, []);

    const showError = useCallback((message) => {
        notification.open({
            message,
            icon: <CloseCircleOutlined style={{ color: 'var(--error-red)' }} />,
            duration: 3,
        });
    }, []);

    const onFinish = useCallback(async (values) => {
        try {
            setLoading(true);
            const response = await ajaxService.post('/request-otp', values);
            const { success, data } = response;

            if (success) {
                localStorage.setItem('forgotPasswordEmail', values.email);
                history.push({ pathname: '/verify-otp' });
                showSuccess(data.status || 'OTP sent successfully');
            } else {
                showError(data?.error || 'Failed to send OTP');
            }
        } catch (error) {
            console.error('Forgot password error:', error);
            showError('An unexpected error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [history, showSuccess, showError]);

    const onFinishFailed = useCallback((errorInfo) => {
        console.log('Form validation failed:', errorInfo);
        if (errorInfo.errorFields.length > 0) {
            showError('Please check your input and try again.');
        }
    }, [showError]);

    const headerRenderer = (props) => {
        const { iconSize, spacing, titleSize, textSize, isSmallHeight } = props;
        return (
            <>
                <ForgotPasswordIcon style={{ 
                    height: iconSize, 
                    display: 'block',
                    margin: `0 auto ${spacing}`,
                }} />
                <Title
                    level={3}
                    style={{ 
                        margin: `${spacing} 0 0 0`, 
                        color: 'var(--text-white)',
                        fontSize: titleSize,
                        fontWeight: 700,
                    }}
                >
                    Forgot Password
                </Title>
                <Text 
                    style={{ 
                        color: 'var(--text-gray-light)', 
                        fontSize: textSize,
                        display: isSmallHeight ? 'none' : 'block',
                        marginTop: spacing,
                    }}
                >
                    Enter your email address for instruction to be sent
                </Text>
            </>
        );
    };

    const formContent = (props) => {
        const { formItemStyle, buttonItemStyle, dividerItemStyle, forgotDivStyle, textSize, formSize, spacing, fieldGap, buttonHeight, buttonFont } = props;
        return (
            <Form
                form={form}
                onFinish={onFinish}
                onFinishFailed={onFinishFailed}
                layout="vertical"
                size={formSize}
                autoComplete="on"
                aria-label="Forgot password form"
                style={{ margin: 0 }}
            >
                <Form.Item
                    name="email"
                    label={
                        <span style={{ 
                            color: 'var(--text-white)', 
                            fontWeight: 500, 
                            fontSize: textSize,
                            display: 'block',
                            marginBottom: '1px',
                        }}>
                            Email Address
                        </span>
                    }
                    rules={[
                        { type: 'email', message: 'The input is not valid email!' },
                        { required: true, message: 'Please input your email!' },
                    ]}
                    hasFeedback
                    style={formItemStyle}
                >
                    <Input 
                        type="email" 
                        placeholder='example123@gmail.com' 
                        allowClear
                        autoComplete="email"
                        style={{
                            borderRadius: 'var(--radius-lg)',
                            background: 'var(--dark-bg-card)',
                            border: '1px solid var(--border-light)',
                            color: 'var(--text-white)',
                        }}
                    />
                </Form.Item>

                <Form.Item style={{...buttonItemStyle, marginTop: fieldGap }}>
                    <Button 
                        loading={loading} 
                        type="primary" 
                        htmlType="submit" 
                        block
                        size={formSize}
                        style={{ 
                            background: 'var(--gradient-primary)',
                            border: 'none',
                            marginTop: spacing,
                            borderRadius: 'var(--radius-lg)',
                            height: buttonHeight,
                            fontWeight: 600,
                            fontSize: buttonFont,
                            boxShadow: '0 4px 14px 0 rgba(99, 102, 241, 0.3)',
                        }}
                        aria-label={loading ? 'Sending OTP' : 'Send OTP'}
                    >
                        {loading ? 'Sending...' : 'Send'}
                    </Button>
                </Form.Item>

                <Divider 
                    plain 
                    style={dividerItemStyle}
                >
                    <Text style={{ color: 'var(--text-gray-light)', fontSize: textSize }}>or</Text>
                </Divider>

                <div style={forgotDivStyle}>
                    <Button
                        type="link"
                        onClick={() => history.push('/login')}
                        style={{
                            color: 'var(--primary-purple-light)',
                            fontWeight: 500,
                            padding: 0,
                            fontSize: textSize,
                        }}
                        aria-label="Back to login"
                    >
                        Back to Login?
                    </Button>
                </div>
            </Form>
        );
    };

    return (
        <AuthLayout
            headerRenderer={headerRenderer}
            formContent={formContent}
            onError={showError}
            onSuccess={showSuccess}
        />
    );
}

export default React.memo(ForgotPassword)